/**
 * Reports – TAT Report.
 * GET /histopathology/reports/tat/?from_date&to_date[&export=pdf]
 * Response totals: avg_tat (string like "2H:15M").
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Stack, Paper, Typography, TextField, Tooltip, Button } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { DateTime } from "luxon";
import RestAPIService from "../../Utility/Services/RestAPIService";
import { Serviceurls } from "../../Utility/API/Serviceurls";
import ToastMsg from "../../Components/ToastMsg/ToastMsg";
import { CommonGridHeader } from "../../Components/Common Components/CommonComponents";
import CommonDatePicker from "../../Components/Common Components/CommonDatePicker";
import "../../Styles/Configuration.css";

const today = () => DateTime.now().toFormat("yyyy-MM-dd");

const SummaryCard = ({ label, value }) => (
  <Paper elevation={1} sx={{ px: 1.2, py: 0.9, minWidth: "8vw", borderRadius: "8px" }}>
    <Typography sx={{ fontSize: "1.1vw", fontWeight: 700, color: "#1a1a1a", lineHeight: 1.15 }}>{value}</Typography>
    <Typography sx={{ fontSize: "0.85vw", color: "#666" }}>{label}</Typography>
  </Paper>
);

const formatDateTime = (v) => {
  if (!v) return "-";
  if (typeof v === "string" && /\d{2}-\d{2}-\d{4}/.test(v)) return v;
  const d = DateTime.fromISO(v);
  return d.isValid ? d.toFormat("dd-MM-yyyy hh:mm a") : "-";
};

export default function TATReport() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [fromDate, setFromDate] = useState(today());
  const [toDate, setToDate] = useState(today());
  const [dateIndex, setDateIndex] = useState(1);
  const [search, setSearch] = useState("");
  const [avgTat, setAvgTat] = useState("0H:0M");
  const [tatCounts, setTatCounts] = useState({ total: 0, withTat: 0, withoutTat: 0 });
  const [msg, setMsg] = useState({ err: false, errText: "", ok: false, okText: "" });

  const buildQuery = useCallback(() => {
    const params = new URLSearchParams();
    if (fromDate) params.append("from_date", fromDate);
    if (toDate) params.append("to_date", toDate);
    return params.toString();
  }, [fromDate, toDate]);

  const fetchData = useCallback(() => {
    setLoading(true);
    const url = `${Serviceurls.HISTOPATHOLOGY_REPORTS_TAT}?${buildQuery()}`;
    RestAPIService.getAll(url)
      .then((res) => {
        if (res?.data?.status === "success") {
          setRows(res.data.data ?? []);
          const t = res.data.totals || {};
          setAvgTat(t.average_tat ?? res.data.avg_tat ?? "0H:0M");
          setTatCounts({
            total: +t.total_count || 0,
            withTat: +t.with_tat_count || 0,
            withoutTat: +t.without_tat_count || 0,
          });
        } else {
          setRows([]);
          setAvgTat("0H:0M");
          setTatCounts({ total: 0, withTat: 0, withoutTat: 0 });
        }
        setLoading(false);
      })
      .catch((err) => {
        setLoading(false);
        setRows([]);
        setAvgTat("0H:0M");
        setTatCounts({ total: 0, withTat: 0, withoutTat: 0 });
        setMsg({ err: true, errText: err?.response?.data?.message || err?.message, ok: false, okText: "" });
      });
  }, [buildQuery]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const filtered = useMemo(() => {
    if (!search) return rows;
    const q = search.toLowerCase();
    return rows.filter((r) =>
      [r.patient_name, r.nxt_id, r.sample_id, r.test_details, r.bill_number, r.approved_by, r.result_type]
        .filter(Boolean).some((v) => String(v).toLowerCase().includes(q))
    );
  }, [rows, search]);

  const handleExportPdf = () => {
    const url = `${Serviceurls.HISTOPATHOLOGY_REPORTS_TAT}?${buildQuery()}&export=pdf`;
    RestAPIService.PrintPDF(url)
      .then((res) => {
        const file = new Blob([res.data], { type: "application/pdf" });
        window.open(URL.createObjectURL(file));
      })
      .catch(() => setMsg({ err: true, errText: "Unable to export PDF.", ok: false, okText: "" }));
  };

  const columns = [
    {
      field: "patient_details", headerName: "Patient Details", flex: 1.4, minWidth: 200, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3, minWidth: 0, width: "100%", overflow: "hidden" }}>
          <Tooltip title={(p.row.patient_name || "-").toUpperCase()} placement="top" arrow>
            <Typography
              variant="body2"
              fontWeight={600}
              sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", textTransform: "uppercase" }}
            >
              {p.row.patient_name || "-"}
            </Typography>
          </Tooltip>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", display: "block" }}
          >
            {[p.row.nxt_id, p.row.patient_age, p.row.patient_gender].filter(Boolean).join(" | ") || "-"}
          </Typography>
        </Box>
      ),
    },
    {
      field: "bill_details", headerName: "Bill Details", flex: 1.1, minWidth: 150, sortable: false,
      renderCell: (p) => (
        <Box sx={{ lineHeight: 1.3 }}>
          <Typography variant="body2">{p.row.bill_number || "-"}</Typography>
          <Typography variant="caption" color="text.secondary">{p.row.invoice_date || "-"}</Typography>
        </Box>
      ),
    },
    {
      field: "sample_id", headerName: "SID", flex: 0.9, minWidth: 110,
      renderCell: (p) => (
        <Tooltip title={p.row.sample_id || "-"} placement="top" arrow>
          <Typography
            variant="body2"
            sx={{ width: "100%", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
          >
            {p.row.sample_id || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "test_details", headerName: "Test", flex: 1.3, minWidth: 160,
      renderCell: (p) => (
        <Tooltip title={p.row.test_details || ""} placement="top-start" arrow>
          <Typography
            variant="body2"
            sx={{
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              width: "100%",
            }}
          >
            {p.row.test_details || "-"}
          </Typography>
        </Tooltip>
      ),
    },
    {
      field: "result_type", headerName: "Type", flex: 0.7, minWidth: 90,
      renderCell: (p) => (
        <Typography sx={{ fontSize: "0.85vw", textTransform: "capitalize" }}>
          {p.row.result_type || "-"}
        </Typography>
      ),
    },
    {
      field: "collected_at", headerName: "Collected At", flex: 1.2, minWidth: 160,
      valueGetter: (value, row) => row.collected_at || formatDateTime(row.sample_received_date),
    },
    {
      field: "approved_details", headerName: "Approved Details", flex: 1.2, minWidth: 170, sortable: false,
      renderCell: (p) => {
        const at = p.row.approved_at || formatDateTime(p.row.completed_date_time);
        const by = p.row.approved_by;
        return (
          <Box sx={{ lineHeight: 1.3, minWidth: 0, width: "100%", overflow: "hidden" }}>
            <Tooltip title={by || "-"} placement="top" arrow>
              <Typography
                variant="body2"
                sx={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
              >
                {by || "-"}
              </Typography>
            </Tooltip>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}
            >
              {at || "-"}
            </Typography>
          </Box>
        );
      },
    },
    {
      field: "tat", headerName: "Actual TAT", flex: 0.8, minWidth: 100,
      renderCell: (p) => (
        <Typography sx={{ fontSize: "0.85vw", color: p.row.tat ? "#0288d1" : "#999", fontWeight: 600 }}>
          {p.row.tat || "-"}
        </Typography>
      ),
    },
  ];

  return (
    <Box sx={{ p: "0.8vw", height: "100%", display: "flex", flexDirection: "column", gap: "0.8vw", overflow: "hidden", minHeight: 0 }}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <SummaryCard label="Avg TAT Time" value={avgTat} />
        <SummaryCard label="Total" value={tatCounts.total} />
        <Box sx={{ flexGrow: 1 }} />
        <Box sx={{ minWidth: "10vw" }}>
          <CommonDatePicker
            FromDate={fromDate} ToDate={toDate} DateIndex={dateIndex}
            HideAllMenu removeMarginRight
            SelectedDates={(from, to, idx) => { setFromDate(from); setToDate(to); setDateIndex(idx); }}
          />
        </Box>
        <TextField size="small" placeholder="Search" value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: "11vw" }} />
        <Tooltip title="Export PDF">
          <Button variant="outlined" size="small" startIcon={<FileDownloadOutlinedIcon />}
            onClick={handleExportPdf} sx={{ textTransform: "capitalize" }}>
            PDF
          </Button>
        </Tooltip>
      </Stack>
      <Paper sx={{ flex: 1, minHeight: 0, overflow: "hidden", display: "flex" }}>
        <DataGrid
          rows={filtered.map((r, i) => ({ ...r, id: r.id ?? `row-${i}` }))}
          columns={columns}
          loading={loading}
          paginationModel={{ page, pageSize }}
          onPaginationModelChange={(m) => { setPage(m.page); setPageSize(m.pageSize); }}
          pageSizeOptions={[10, 20, 30]}
          disableRowSelectionOnClick
          rowHeight={56}
          columnHeaderHeight={42}
          slots={{
            toolbar: CommonGridHeader.CommonToolBar,
            noRowsOverlay: () => <Stack className="eMed_conf_nodata">No Records Found</Stack>,
          }}
          sx={{ border: 0, flex: 1, minWidth: 0 }}
        />
      </Paper>
      {msg.err && <ToastMsg severity="error" msg={msg.errText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
      {msg.ok && <ToastMsg severity="success" msg={msg.okText} msgPop={() => setMsg({ err: false, errText: "", ok: false, okText: "" })} />}
    </Box>
  );
}
